import React, { useState, useEffect } from "react";
import {
  doc,
  getDoc,
  setDoc,
  collection,
  getDocs,
  Timestamp,
} from "firebase/firestore";
import { db } from "../firebase";
import { v4 as uuidv4 } from "uuid";  // at top of file

function normalizeText(text) {
  return text
    .toLowerCase()
    .trim()
    .replace(/\s+/g, " ")
    .replace(/[^\w\s]/g, "");
}


// Helper to parse expiry text
const parseExpiryText = (text) => {
  if (!text) return null;
  const now = new Date();
  text = text.toLowerCase();

  if (text.includes("expires today")) {
    now.setHours(23, 59, 59, 999);
    return now;
  }

  const match = text.match(/(\d+)d left|expires in (\d+) days/);
  if (match) {
    const days = parseInt(match[1] || match[2], 10);
    const expiry = new Date(now.getTime() + days * 86400000);
    expiry.setHours(23, 59, 59, 999);
    return expiry;
  }

  const dateMatch = text.match(/\d{2}\/\d{2}\/\d{4}/);
  if (dateMatch) {
    const [mm, dd, yyyy] = dateMatch[0].split("/");
    return new Date(`${yyyy}-${mm}-${dd}T23:59:59`);
  }

  const altDateMatch = text.match(/exp\.\s*(\d{2}\/\d{2}\/\d{2,4})/i);
  if (altDateMatch) {
    const [mm, dd, yyyy] = altDateMatch[1].split("/");
    const fullYear = yyyy.length === 2 ? `20${yyyy}` : yyyy;
    return new Date(`${fullYear}-${mm}-${dd}T23:59:59`);
  }

  // Handle "Expires 10/30/25" or "Ends 11/05/25"
  const simpleExpiry = text.match(/(?:expires|ends)\s+(\d{1,2})\/(\d{1,2})\/(\d{2,4})/i);
  if (simpleExpiry) {
    let [, mm, dd, yy] = simpleExpiry;
    const yyyy = yy.length === 2 ? `20${yy}` : yy;
    const d = new Date(`${yyyy}-${mm}-${dd}T23:59:59`);
    if (!isNaN(d.getTime())) return d; // ✅ only return if valid
  }

  return null;
};

//Amex
const parseAmexOffersFromHtml = (html) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");

  // Broaden selector to catch both old and new layouts
  const offerRows = doc.querySelectorAll('[data-rowtype="offer"], ._listViewRow_tupp2_26');
  const offers = [];

  offerRows.forEach((offerRow) => {
    let offer = null;
    let vendor = null;
    let icon = null;
    let expiryDttm = null;

    // --- Old layout (unchanged)
    const oldLogoImg = offerRow.querySelector(".merchant-logo img");
    const oldDescription = offerRow.querySelector(".offer-info p:nth-of-type(1)");
    const oldVendor = offerRow.querySelector(".offer-info p:nth-of-type(2)");
    const oldExpiry = offerRow.querySelector(".offer-expires span span, .offer-expires span");

    if (oldDescription && oldVendor && oldLogoImg) {
      offer = oldDescription.textContent.trim();
      icon = oldLogoImg.src.trim();
      const expiryRaw = oldExpiry?.textContent?.trim();
      expiryDttm = parseExpiryText(expiryRaw);
      vendor = cleanVendorName(oldVendor.textContent);
    }

    // --- New layout (Cardlytics-style)
    if (!offer || !vendor || !icon) {
      const newLogoImg = offerRow.querySelector("img");
      const newVendor = offerRow.querySelector("h3.heading-sans-small-medium span");
      const newOffer = offerRow.querySelector("div.body.color-text-regular span");
      const newExpiry = offerRow.querySelector("p.body-small");

      if (newLogoImg && newVendor && newOffer) {
        icon = newLogoImg.src.trim();
        vendor = cleanVendorName(newVendor.textContent);
        offer = newOffer.textContent.trim();

        const expiryRaw = newExpiry?.textContent?.trim();
        expiryDttm = parseExpiryText(expiryRaw);
      }
    }

    // --- Extract spend/get pattern
    let minimumSpend = null;
    let maximumOffer = null;
    if (offer) {
      const match = offer.match(/Spend\s*\$([\d,.]+)(?:\s*or more)?,?\s*(?:get|earn)\s*\$([\d,.]+)/i);
      if (match) {
        minimumSpend = parseFloat(match[1].replace(/,/g, ""));
        maximumOffer = parseFloat(match[2].replace(/,/g, ""));
      }
    }

    // --- Push only valid offers
    if (vendor && offer && icon && !/wine|bottle/i.test(vendor)) {
      offers.push({
        vendor,
        offer,
        icon,
        expiryDttm,
        minimumSpend,
        maximumOffer,
      });
    }
  });

  return offers;
};


const cleanVendorName = (rawText) => {
  let vendorText = rawText?.trim() || "";
  vendorText = vendorText.replace(/\s*\(.*?\)\s*/g, ""); // Remove parentheses
  vendorText = vendorText.split(/[-–—/]/)[0].trim(); // Take before hyphen/slash
  return vendorText;
};

// Parser for PayPal with minimum 3% filter
const parsePayPalOffersFromHtml = (html) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");

  const offerContainers = doc.querySelectorAll(".saved-offers-150a24d");
  const offers = [];

  offerContainers.forEach((container) => {
    const vendor = container.querySelector(".saved-offers-0 p")?.textContent?.trim();
    const icon = container.querySelector(".saved-offers-1ihyxqm img")?.src?.trim();
    const offerText = container.querySelector(".saved-offers-0 p.saved-offers-1fhgjcy")?.textContent?.trim();
    const expiryText = container.querySelector(".saved-offers-1c7ucuw")?.textContent?.trim();

    if (!vendor || !icon || !offerText || !expiryText) return;

    // Parse the percentage from offerText, e.g., "2.5% cash back"
    const percentMatch = offerText.match(/(\d+(\.\d+)?)%/);
    const percent = percentMatch ? parseFloat(percentMatch[1]) : 0;

    // Skip offers with less than 3%
    if (percent < 3) return;

    // Convert expiry text to actual Date
    const expiryDttm = parsePayPalExpiry(expiryText);

    offers.push({
      vendor,
      offer: offerText,
      icon,
      expiryDttm,
    });
  });

  return offers;
};

// Helper to parse PayPal expiry text like "2 days left"
const parsePayPalExpiry = (text) => {
  const now = new Date();
  let daysToAdd = 0;

  const dayMatch = text.match(/(\d+)\s*day/i);
  const weekMatch = text.match(/(\d+)\s*week/i);
  const hourMatch = text.match(/(\d+)\s*hour/i);

  if (dayMatch) daysToAdd = parseInt(dayMatch[1], 10);
  else if (weekMatch) daysToAdd = parseInt(weekMatch[1], 10) * 7;
  else if (hourMatch) daysToAdd = parseInt(hourMatch[1], 10) / 24;

  const expiry = new Date(now);
  expiry.setDate(expiry.getDate() + daysToAdd);
  return expiry;
};


// Parser for Bank of America
const parseBofAOffersFromHtml = (html) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");

  const offerContainers = doc.querySelectorAll(".available-deal");
  const offers = [];

  offerContainers.forEach((container) => {
    const vendor = container.querySelector(".company_name")?.textContent?.trim();
    const icon = container.querySelector(".deal-logo img")?.src?.trim();
    const offerText = container.querySelector(".deal-offer-percent")?.textContent?.trim();
    const expiryText = container.querySelector(".deal-exp-date")?.textContent?.trim();

    const cleanOfferMatch = offerText?.match(/(\$\d+(\.\d+)?|\d+%)/);
    const cleanOffer = cleanOfferMatch ? cleanOfferMatch[0] : offerText;

    const expiry = parseExpiryText(expiryText);
    if (!vendor || !icon || !offerText || !expiryText || !expiry) return;

    const descContainer = container.querySelector(".summary-desc");
    const descParas = descContainer?.querySelectorAll("p") || [];

    let maximumOffer = null;
    let minimumSpend = null;

    descParas.forEach((p) => {
      const text = p.textContent;

      if (!maximumOffer) {
        const maxMatch = text.match(/maximum (of )?\$?(\d+(\.\d+)?)/i);
        if (maxMatch) maximumOffer = `$${maxMatch[2]}`;
      }

      if (!minimumSpend) {
        const minMatch = text.match(/minimum (purchase )?(of )?\$?(\d+(\.\d+)?)/i) ||
          text.match(/spend \$?(\d+(\.\d+)?)/i);
        if (minMatch) minimumSpend = `$${minMatch[1] || minMatch[3]}`;
      }
    });

    offers.push({
      vendor,
      offer: cleanOffer,
      icon,
      expiryDttm: expiry,
      maximumOffer,
      minimumSpend,
    });
  });

  return offers;
};

// Parser for Capital One (no expiry, clear and reload)
const parseCapitalOneOffersFromHtml = (html) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");

  const offers = [];

  // Each offer tile
  const tiles = doc.querySelectorAll(".standard-tile");
  tiles.forEach((tile) => {
    const img = tile.querySelector("img");
    if (!img) return;

    // Extract domain from img src param
    const srcUrl = new URL(img.src);
    const domainParam = srcUrl.searchParams.get("domain") || "unknown";

    // Vendor normalized to domain name (e.g. lowes.com)
    const vendor = domainParam.toLowerCase();

    // Offer text (green text div)
    const offerDiv = tile.querySelector("div[style*='color: rgb(37, 129, 14)']");
    const offerText = offerDiv?.textContent?.trim() || "";

    offers.push({
      vendor,
      offer: offerText,
      icon: img.src,
      expiryDttm: null, // No expiry available
    });
  });

  return offers;
};

// Parser for Citi Bank
const parseCitiOffersFromHtml = (html) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");

  const tiles = doc.querySelectorAll("app-mo-offer-tile");
  const offers = [];

  tiles.forEach((tile) => {
    const merchantNameElem = tile.querySelector(".merchant-name");
    const offerButton = tile.querySelector(".offer-title-oneclick button");
    const expiryElem = tile.querySelector(".tile-content-desc.text-danger");

    if (!merchantNameElem || !offerButton) return;

    const vendor = merchantNameElem.textContent.trim();
    const offer = offerButton.textContent.trim();
    const expiryDttm = parseExpiryText(expiryElem?.textContent?.trim() || "");

    // helper function to check excluded vendors
    const isExcludedVendor = (vendor) => {
      const lower = vendor.toLowerCase();
      return lower.includes("wine") || lower.includes("whiskey") || lower.includes("bottle");
    };

    if (vendor && offer && !isExcludedVendor(vendor)) {
      offers.push({
        vendor,
        offer,
        icon: tile.querySelector(".offer-image img")?.src || "",
        expiryDttm,
      });
    }
  });

  return offers;
};

//Chase bank parser
const parseChaseOffersFromHtml = (html) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  const offers = [];

  // helper function to check excluded vendors
  const isExcludedVendor = (vendor) => {
    const lower = vendor.toLowerCase();
    return lower.includes("wine") || lower.includes("whiskey") || lower.includes("bottle");
  };

  // OLD format: data-testid="commerce-tile"
  const oldFormatTiles = doc.querySelectorAll('[data-testid="commerce-tile"]');
  oldFormatTiles.forEach((tile) => {
    const vendor = tile.querySelector(".r9jbijk")?.textContent?.trim();
    const offerText = tile.querySelector(".r9jbijj")?.textContent?.trim();
    const icon = tile.querySelector('[data-testid="logo-container"] img')?.src?.trim();
    const expiryText = tile.querySelector('[data-testid="days-left-banner"]')?.textContent?.trim();

    const expiryDttm = parseExpiryText(expiryText);

    if (vendor && offerText && icon && !isExcludedVendor(vendor)) {
      offers.push({
        vendor,
        offer: offerText,
        icon,
        expiryDttm,
      });
    }
  });

  // NEW format: div.z9bqz54 role=listitem
  const newFormatTiles = doc.querySelectorAll('div.z9bqz54[role="listitem"]');
  newFormatTiles.forEach((tile) => {
    // vendor: <span class="mds-body-small-heavier r9jbijk ...">...</span>
    const vendor = tile.querySelector("span.mds-body-small-heavier")?.textContent?.trim();
    // offer: <span class="mds-body-large-heavier r9jbijj ...">...</span>
    const offerText = tile.querySelector("span.mds-body-large-heavier")?.textContent?.trim();
    // icon: logo container img
    const icon = tile.querySelector("div.r9jbij4.r9jbij5 img")?.src?.trim() ||
      tile.querySelector("img.r9jbij2")?.src?.trim(); // fallback to main image if logo missing
    // expiry: div with class .mds-body-small-heavier.semanticColorTextError
    const expiryText = tile.querySelector("div.mds-body-small-heavier.semanticColorTextError")?.textContent?.trim();

    const expiryDttm = parseExpiryText(expiryText);

    if (vendor && offerText && icon) {
      offers.push({
        vendor,
        offer: offerText,
        icon,
        expiryDttm,
      });
    }
  });

  return offers;
};



const fetchVendors = async () => {
  const snapshot = await getDocs(collection(db, "vendors"));
  const vendorMap = {};

  snapshot.forEach((doc) => {
    const data = doc.data();
    if (Array.isArray(data.vendors)) {
      data.vendors.forEach((vendor) => {
        const name = vendor.vendorName || vendor.id;
        if (name) {
          vendorMap[normalizeVendorName(name)] = name;
        }
      });
    }
  });
  return vendorMap;
};



// Extract main vendor name from strings like "Blueland - Refillable Cleaning Products"
function extractMainVendorName(vendorFullName) {
  // Split by dash, colon, or comma and take the first part
  const mainPart = vendorFullName.split(/[-:,]/)[0].trim();
  return mainPart;
}

// Normalize vendor name for matching keys
function normalizeVendorName(name) {
  return name
    .toLowerCase()
    .replace(/\.com$/, "")        // remove trailing .com
    .replace(/&/g, "and")         // replace & with and
    .replace(/['’]/g, "")         // remove apostrophes
    .replace(/\s+/g, "")          // remove all spaces
    .replace(/[^a-z0-9]/g, "")    // remove non-alphanumerics
    .trim();
}


export default function AddBankOffer({ uploadedBy }) {
  const [file, setFile] = useState(null);
  const [fileType, setFileType] = useState("html"); // "html" or "json"
  const [banks, setBanks] = useState([]);
  const [bankName, setBankName] = useState("");
  const [mode, setMode] = useState("append"); // New: Upload mode
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchBanks = async () => {
      try {
        const snapshot = await getDocs(collection(db, "banks"));
        const list = [];
        snapshot.forEach((doc) => list.push({ id: doc.id, ...doc.data() }));
        setBanks(list);
        if (list.length > 0) setBankName(list[0].id);
      } catch (err) {
        console.error("Error loading banks:", err);
        setStatus("❌ Failed to load banks.");
      }
    };
    fetchBanks();
  }, []);

  // Vendors you want to always ignore quietly (not matched, not added to errors)
  const IGNORE_VENDOR_LIST = [
    "f1 mexico city grand prix: on",
    "laithwaites",
    "bestbullysticks.com",
    "eddie merlot's",
    "jersey mike's",
    "grüns",
    "dine in the uk",
    "cakes body",
    "ode à la rose",
    "ocean prime",
    "estéelauder.com",
    "relais & châteaux",
    "bevmo",
    "telecharge",
    "cakebread cellars",
    "kendall",
    "sunlighten.com",
    "served neat",
    "warby parker",
    "raffles, fairmont, sls, sofitel & legend",
  ];

  const IGNORE_VENDOR_LIST_NORMALIZED = IGNORE_VENDOR_LIST.map(normalizeVendorName);

  const handleUpload = async () => {
    if (!file || !bankName) {
      setStatus("❌ Select both a file and a bank.");
      return;
    }

    setLoading(true);
    setStatus("");

    try {
      const normalizedBank = bankName.toLowerCase();
      let offers = [];

      if (fileType === "html") {
        const text = await file.text();

        if (normalizedBank === "american express") {
          offers = parseAmexOffersFromHtml(text);
        } else if (normalizedBank === "bank of america") {
          offers = parseBofAOffersFromHtml(text);
        } else if (normalizedBank === "capital one") {
          offers = parseCapitalOneOffersFromHtml(text);
        } else if (normalizedBank === "citi") {
          offers = parseCitiOffersFromHtml(text);
        } else if (normalizedBank === "chase") {
          offers = parseChaseOffersFromHtml(text);
        } else if (normalizedBank === "paypal") {
          offers = parsePayPalOffersFromHtml(text);
        }
        else {
          setStatus("❌ Parser not available for selected bank.");
          setLoading(false);
          return;
        }

        if (offers.length === 0) {
          setStatus("⚠️ No valid offers found in HTML.");
          setLoading(false);
          return;
        }

        const vendorMap = await fetchVendors();
        const unmatchedVendors = new Set();

        const bankDocRef = doc(db, "bank_offers", bankName);
        const existingDocSnap = await getDoc(bankDocRef);
        const existingOffers = existingDocSnap.exists()
          ? existingDocSnap.data().offers || []
          : [];

        const now = new Date();
        const activeOffers = existingOffers.filter((o) => {
          if (!o.expiryDttm) return true; // No expiry means still valid
          const expiryDate = o.expiryDttm.toDate?.() || new Date(o.expiryDttm);
          return expiryDate >= now;
        });

        const existingSet = new Set(
          activeOffers.map(
            (o) => `${normalizeVendorName(o.vendor)}|${normalizeText(o.offer)}`
          )
        );


        const newOffers = [];
        let uploadedCount = 0;
        let duplicateCount = 0;

        const parsedSet = new Set(); // track duplicates in the uploaded batch 

        for (const offer of offers) {
          if (!offer.offer || offer.offer.trim() === "") continue;

          const mainVendor = extractMainVendorName(offer.vendor);
          const normalizedOfferVendor = normalizeVendorName(mainVendor);
          const normalizedOfferText = normalizeText(offer.offer);

          // Check duplicate in uploaded file itself
          const dedupKeyParsed = `${normalizedOfferVendor}|${normalizedOfferText}`;
          if (parsedSet.has(dedupKeyParsed)) {
            duplicateCount++;
            continue;
          }
          parsedSet.add(dedupKeyParsed);

          // Vendor mapping fallback
          let normalizedVendor = vendorMap[normalizedOfferVendor];
          if (!normalizedVendor) {
            const fallbackKey = Object.keys(vendorMap).find(
              (key) =>
                normalizedOfferVendor.includes(key) || key.includes(normalizedOfferVendor)
            );
            if (fallbackKey) {
              normalizedVendor = vendorMap[fallbackKey];
            }
          }

          if (!normalizedVendor) {
            const vendorLower = normalizedOfferVendor.toLowerCase();

            // Skip if vendor has "wine" or "whiskey" or is in ignore list
            const isIgnored =
              vendorLower.includes("wine") ||
              vendorLower.includes("whiskey") ||
              IGNORE_VENDOR_LIST_NORMALIZED.includes(vendorLower);

            if (!isIgnored) {
              unmatchedVendors.add(offer.vendor);
            }
            continue;
          }

          // Check duplicate against existing DB offers
          const dedupKeyExisting = `${normalizedOfferVendor}|${normalizedOfferText}`;
          if (mode === "append" && existingSet.has(dedupKeyExisting)) {
            duplicateCount++;
            continue;
          }

          // Add new offer
          const offerObj = {
            id: uuidv4(),
            vendor: offer.vendor,
            offer: offer.offer,
            icon: offer.icon,
            uploadedDttm: Timestamp.now(), 
            bankName,
          };

          // add expiry only if present
          if (offer.expiryDttm) {
            offerObj.expiryDttm = Timestamp.fromDate(offer.expiryDttm);
          }

          // add min spend only if present
          if (offer.minimumSpend !== null && offer.minimumSpend !== undefined) {
            offerObj.minimumSpend = offer.minimumSpend;
          }

          // add max offer only if present
          if (offer.maximumOffer !== null && offer.maximumOffer !== undefined) {
            offerObj.maximumOffer = offer.maximumOffer;
          }

          newOffers.push(offerObj);

          uploadedCount++;
        }


        const mergedOffers =
          mode === "append" ? [...existingOffers, ...newOffers] : newOffers;

        await setDoc(bankDocRef, { offers: mergedOffers });

        let summary = `✅ Uploaded ${uploadedCount} offers to ${bankName}.`;
        if (duplicateCount > 0)
          summary += `\n🔁 ${duplicateCount} duplicate offers skipped.`;
        if (unmatchedVendors.size > 0)
          summary += `\n🚫 Vendor match failed for:\n${[...unmatchedVendors].join(
            "\n"
          )}`;

        setStatus(summary);
      } else if (fileType === "json") {
        const text = await file.text();
        let jsonOffers;

        try {
          jsonOffers = JSON.parse(text);
          if (!Array.isArray(jsonOffers)) {
            setStatus("❌ JSON file must contain an array of offers.");
            setLoading(false);
            return;
          }
        } catch (err) {
          setStatus("❌ Invalid JSON file.");
          setLoading(false);
          return;
        }

        const bankDocRef = doc(db, "bank_offers", bankName);
        const existingDocSnap = await getDoc(bankDocRef);
        const existingOffers = existingDocSnap.exists()
          ? existingDocSnap.data().offers || []
          : [];

        let mergedOffers = [];

        if (mode === "append") {
          mergedOffers = [...existingOffers];
          for (const offer of jsonOffers) {
            mergedOffers.push({
              ...offer,
              id: offer.id || uuidv4(),
              expiryDttm: offer.expiryDttm
                ? offer.expiryDttm.seconds
                  ? offer.expiryDttm
                  : Timestamp.fromDate(new Date(offer.expiryDttm))
                : null,
              uploadedDttm: Timestamp.now(),
              uploadedBy: uploadedBy || "admin@example.com",
              bankName,
            });
          }
        } else {
          mergedOffers = jsonOffers.map((offer) => ({
            ...offer,
            id: offer.id || uuidv4(),
            expiryDttm: offer.expiryDttm
              ? offer.expiryDttm.seconds
                ? offer.expiryDttm
                : Timestamp.fromDate(new Date(offer.expiryDttm))
              : null,
            uploadedDttm: Timestamp.now(),
            uploadedBy: uploadedBy || "admin@example.com",
            bankName,
          }));
        }

        await setDoc(bankDocRef, { offers: mergedOffers });

        setStatus(`✅ Uploaded ${jsonOffers.length} offers to ${bankName} (JSON upload).`);
      }
    } catch (err) {
      console.error("Upload error:", err);
      setStatus("❌ Upload failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-md max-w-md mx-auto">
      <h2 className="text-2xl font-semibold mb-4">🏦 Upload Bank Offers</h2>

      <div className="space-y-4">
        <input
          type="file"
          accept={fileType === "html" ? ".html" : ".json"}
          onChange={(e) => {
            const selectedFile = e.target.files[0];
            setFile(selectedFile);

            if (selectedFile) {
              const fileName = selectedFile.name.toLowerCase();

              // Mapping from keywords to proper display names
              const bankMap = {
                bankofamerica: "Bank Of America",
                boa: "Bank Of America",
                capitalone: "Capital One",
                chase: "Chase",
                citi: "Citi",
                amex: "American Express",
                americanexpress: "American Express"
              };

              // Try to match file name to a known bank
              for (const key in bankMap) {
                if (fileName.includes(key)) {
                  setBankName(bankMap[key]);
                  break;
                }
              }
            }
          }}
          className="block w-full"
        />


        <select
          value={fileType}
          onChange={(e) => {
            setFileType(e.target.value);
            setFile(null); // Clear file on type change
            setStatus("");
          }}
          className="w-full p-2 border rounded"
        >
          <option value="html">HTML File</option>
          <option value="json">JSON File</option>
        </select>

        <select
          value={bankName}
          onChange={(e) => setBankName(e.target.value)}
          className="w-full p-2 border rounded"
        >
          {banks.map((b) => (
            <option key={b.id} value={b.id}>
              {b.id}
            </option>
          ))}
        </select>

        <select
          value={mode}
          onChange={(e) => setMode(e.target.value)}
          className="w-full p-2 border rounded"
        >
          <option value="append">Append</option>
          <option value="full">Full Load</option>
        </select>

        <button
          onClick={handleUpload}
          disabled={loading}
          className="w-full bg-indigo-600 text-white p-2 rounded hover:bg-indigo-700 disabled:opacity-50"
        >
          {loading ? "Uploading..." : "Upload Offers"}
        </button>

        {status && (
          <pre
            className={`whitespace-pre-wrap mt-4 ${status.startsWith("❌") ? "text-red-600" : "text-green-600"
              }`}
          >
            {status}
          </pre>
        )}
      </div>
    </div>
  );
}
